// The middle column of the Files tab: the selected commit (its message, author and size), then
// its changed files or every file as it was at that commit, as a tree. A folder without git
// lists its own files, with a word on why there is no history.
import type { GitCommit } from '@modelviewer/core';
import { Check, Copy, File, FileCode2, Folder, FolderOpen, Info, Search } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { cn } from '../lib/cn';
import { commitPaths, selectFile, useFiles, type RepoState } from '../state/files';
import { TreeItem } from '../views/Explorer';
import { STATUS_COLOR, STATUS_LETTER, ago, body, shortId, subject, when } from './format';

type Mode = 'changed' | 'all';

interface Node {
  name: string;
  path: string;
  folder: boolean;
  children: Node[];
}

const CODE = /\.(cs|csx|ts|tsx|js|jsx|mjs|json|xml|csproj|props|targets|sql|ps1|sh|py|go|rs|java|kt|css|scss|html|cshtml|razor|ya?ml|toml)$/i;

/** Folders first, then files, by name; a folder holding only one folder is drawn as one row (`src/App`). */
function buildTree(paths: string[]): Node[] {
  const root: Node = { name: '', path: '', folder: true, children: [] };
  const folders = new Map<string, Node>([['', root]]);
  for (const path of paths) {
    const parts = path.split('/');
    let parent = root;
    for (let i = 0; i < parts.length - 1; i++) {
      const at = parts.slice(0, i + 1).join('/');
      let folder = folders.get(at);
      if (!folder) {
        folder = { name: parts[i]!, path: at, folder: true, children: [] };
        folders.set(at, folder);
        parent.children.push(folder);
      }
      parent = folder;
    }
    if (!parent.children.some((c) => !c.folder && c.path === path)) parent.children.push({ name: parts.at(-1)!, path, folder: false, children: [] });
  }
  const finish = (node: Node): Node => {
    let n = node;
    while (n.folder && n.path && n.children.length === 1 && n.children[0]!.folder) {
      const only = n.children[0]!;
      n = { ...only, name: `${n.name}/${only.name}` };
    }
    n.children = n.children.map(finish).sort((a, b) => Number(b.folder) - Number(a.folder) || a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }));
    return n;
  };
  return finish(root).children;
}

function ancestors(path: string): string[] {
  const parts = path.split('/');
  return parts.slice(0, -1).map((_, i) => parts.slice(0, i + 1).join('/'));
}

function CopyId({ id }: { id: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={() => {
        void navigator.clipboard?.writeText(id).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1200);
        });
      }}
      className="inline-flex items-center gap-1 rounded px-1 font-mono text-faint hover:bg-hover hover:text-text"
      title={`Copy ${id}`}
      aria-label="Copy the commit id"
    >
      {shortId(id)} {copied ? <Check size={11} /> : <Copy size={11} />}
    </button>
  );
}

function CommitHeader({ commit }: { commit: GitCommit }) {
  const [open, setOpen] = useState(false);
  const rest = body(commit);
  useEffect(() => setOpen(false), [commit.id]);
  return (
    <div className="flex-none border-b border-border px-3 py-2.5" data-testid="commit-header">
      <p className="text-[13px] leading-snug font-medium break-words text-text">{subject(commit)}</p>
      {rest && (
        <>
          <p className={cn('mt-1 text-[12px] leading-relaxed break-words whitespace-pre-wrap text-muted', !open && 'line-clamp-3')}>{rest}</p>
          {rest.split('\n').length > 3 && (
            <button type="button" onClick={() => setOpen(!open)} className="text-[11.5px] text-accent hover:underline">
              {open ? 'Less' : 'More'}
            </button>
          )}
        </>
      )}
      <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-0.5 text-[11.5px] text-faint">
        <span className="text-muted" title={commit.author.email}>
          {commit.author.name}
        </span>
        <span title={when(commit.author)}>{ago(commit.author)}</span>
        {commit.committer.name !== commit.author.name && <span title={`Committed by ${commit.committer.name}, ${when(commit.committer)}`}>· committed by {commit.committer.name}</span>}
        <CopyId id={commit.id} />
        {commit.parents.length > 1 && <span>· merge of {commit.parents.map(shortId).join(' and ')}</span>}
      </div>
    </div>
  );
}

function NoHistory({ repo }: { repo: RepoState }) {
  const text =
    repo.state === 'linked'
      ? `This folder is a linked git worktree (or a submodule): its history is kept in ${repo.gitDir || 'another folder'}, outside the folder you opened. Open the main checkout to see it.`
      : repo.state === 'error'
        ? `The git history could not be read: ${repo.message}`
        : repo.state === 'sample'
          ? 'The sample has no git history. Open a folder that is a git repository to see its commits.'
          : 'This folder is not a git repository, so there is no history to show. Open the folder that holds .git to see its commits.';
  return (
    <div className="m-2 flex flex-none gap-2 rounded-md border border-dashed border-border p-2.5 text-[12px] leading-relaxed text-muted" data-testid="no-history">
      <Info size={14} className="mt-0.5 flex-none text-faint" />
      <span>{text}</span>
    </div>
  );
}

function Counts({ counts }: { counts: { added: number; removed: number } | 'binary' | undefined }) {
  if (counts === undefined) return null;
  if (counts === 'binary') return <span className="text-faint">binary</span>;
  return (
    <span className="font-mono text-[10.5px]">
      {counts.added > 0 && <span className="text-added">+{counts.added}</span>}
      {counts.added > 0 && counts.removed > 0 && ' '}
      {counts.removed > 0 && <span className="text-removed">−{counts.removed}</span>}
    </span>
  );
}

export function FileColumn({ onPick }: { onPick?: () => void }) {
  const repo = useFiles((s) => s.repo);
  const commitId = useFiles((s) => s.commit);
  const commit = useFiles((s) => s.commits.find((c) => c.id === s.commit));
  const changes = useFiles((s) => s.changes);
  const counts = useFiles((s) => s.counts);
  const file = useFiles((s) => s.file);
  const folderFiles = useFiles((s) => s.folderFiles);
  const git = repo.state === 'ready';
  const [chosen, setChosen] = useState<Mode>('changed');
  const mode: Mode = git ? chosen : 'all';
  const [filter, setFilter] = useState('');
  const [toggled, setToggled] = useState<Set<string>>(new Set());
  const [commitFiles, setCommitFiles] = useState<{ commit: string; paths: string[] } | null>(null);

  // Every file of the commit, read when "All files" is shown.
  useEffect(() => {
    if (!git || mode !== 'all' || !commitId || commitFiles?.commit === commitId) return;
    let cancelled = false;
    void commitPaths(commitId).then((paths) => !cancelled && setCommitFiles({ commit: commitId, paths }));
    return () => {
      cancelled = true;
    };
  }, [git, mode, commitId, commitFiles]);

  useEffect(() => setToggled(new Set()), [commitId, mode]);

  const byPath = useMemo(() => new Map((changes ?? []).map((c) => [c.path, c])), [changes]);
  const paths = useMemo(() => {
    if (mode === 'changed') return (changes ?? []).map((c) => c.path);
    const listed = git ? (commitFiles?.commit === commitId ? commitFiles.paths : []) : (folderFiles ?? []);
    // Files the commit deleted are not in it any more; show them where they were.
    const deleted = (changes ?? []).filter((c) => c.status === 'deleted').map((c) => c.path);
    return deleted.length ? [...listed, ...deleted] : listed;
  }, [mode, changes, git, commitFiles, commitId, folderFiles]);
  const tree = useMemo(() => buildTree(paths), [paths]);
  const changedFolders = useMemo(() => new Set((changes ?? []).flatMap((c) => ancestors(c.path))), [changes]);
  const fileFolders = useMemo(() => new Set(file ? ancestors(file) : []), [file]);
  const totals = useMemo(() => {
    let added = 0;
    let removed = 0;
    for (const c of Object.values(counts)) {
      if (c === 'binary') continue;
      added += c.added;
      removed += c.removed;
    }
    return { added, removed };
  }, [counts]);

  const isOpen = (node: Node) => {
    // A row like `src/App` stands for both folders.
    const opened = mode === 'changed' || changedFolders.has(node.path) || fileFolders.has(node.path);
    return toggled.has(node.path) ? !opened : opened;
  };
  const toggle = (path: string) =>
    setToggled((t) => {
      const next = new Set(t);
      if (!next.delete(path)) next.add(path);
      return next;
    });

  const pick = (path: string) => {
    selectFile(path);
    onPick?.();
  };

  const fileRow = (path: string, name: string, depth: number, hint?: string) => {
    const change = byPath.get(path);
    return (
      <TreeItem
        key={path}
        depth={depth}
        icon={CODE.test(path) ? <FileCode2 size={14} /> : <File size={14} />}
        label={
          <span className={cn(change?.status === 'deleted' && 'line-through decoration-removed/60', change && STATUS_COLOR[change.status])}>
            {name}
            {hint && <span className="ml-1.5 text-[11px] text-faint no-underline">{hint}</span>}
          </span>
        }
        title={change?.oldPath ? `${path} (renamed from ${change.oldPath})` : path}
        hint={
          change ? (
            <span className="flex items-center gap-1.5">
              <Counts counts={counts[path]} />
              <span className={cn('w-2.5 text-center font-mono text-[10.5px] font-semibold', STATUS_COLOR[change.status])} title={change.status}>
                {STATUS_LETTER[change.status]}
              </span>
            </span>
          ) : undefined
        }
        active={file === path}
        onClick={() => pick(path)}
      />
    );
  };

  const rows: React.ReactNode[] = [];
  const q = filter.trim().toLowerCase();
  if (q) {
    const matches = paths.filter((p) => p.toLowerCase().includes(q));
    for (const p of matches.slice(0, 500)) {
      const folder = p.includes('/') ? p.slice(0, p.lastIndexOf('/')) : '';
      rows.push(fileRow(p, p.slice(p.lastIndexOf('/') + 1), 0, folder));
    }
    if (matches.length > 500) rows.push(<p key="more" className="px-3 py-1 text-[12px] text-faint">{matches.length - 500} more; type more to narrow it down.</p>);
  } else {
    const walk = (nodes: Node[], depth: number) => {
      for (const node of nodes) {
        if (!node.folder) {
          rows.push(fileRow(node.path, node.name, depth));
          continue;
        }
        const open = isOpen(node);
        rows.push(
          <TreeItem
            key={`${node.path}/`}
            depth={depth}
            icon={open ? <FolderOpen size={14} /> : <Folder size={14} />}
            label={node.name}
            title={node.path}
            hint={mode === 'all' && changedFolders.has(node.path) ? <span className="block h-1.5 w-1.5 rounded-full bg-changed" title="Has changes" /> : undefined}
            expanded={open}
            onToggle={() => toggle(node.path)}
            onClick={() => toggle(node.path)}
          />,
        );
        if (open) walk(node.children, depth + 1);
      }
    };
    walk(tree, 0);
  }

  const reading = (mode === 'changed' && git && changes === null) || (mode === 'all' && git && commitFiles?.commit !== commitId);

  return (
    <div className="flex h-full min-h-0 flex-col">
      {git ? commit && <CommitHeader commit={commit} /> : repo.state !== 'loading' && repo.state !== 'idle' && <NoHistory repo={repo} />}
      <div className="flex flex-none items-center gap-2 px-2 pt-2 pb-1">
        {git && (
          <div role="radiogroup" aria-label="Files to show" className="flex flex-none rounded-md border border-border p-0.5 text-[12px]">
            {(
              [
                ['changed', `Changed${changes ? ` ${changes.length}` : ''}`],
                ['all', 'All files'],
              ] as const
            ).map(([value, label]) => (
              <button
                key={value}
                type="button"
                role="radio"
                aria-checked={mode === value}
                onClick={() => setChosen(value)}
                className={cn('rounded px-2 py-0.5 whitespace-nowrap pointer-coarse:py-1.5', mode === value ? 'bg-active text-text' : 'text-muted hover:text-text')}
              >
                {label}
              </button>
            ))}
          </div>
        )}
        <label className="flex min-w-0 flex-1 items-center gap-1.5 rounded-md border border-border px-2">
          <Search size={12} className="flex-none text-faint" />
          <input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filter files"
            aria-label="Filter files"
            className="h-6 min-w-0 flex-1 bg-transparent text-[12px] outline-none placeholder:text-faint pointer-coarse:h-9"
          />
        </label>
      </div>
      {git && mode === 'changed' && changes && changes.length > 0 && (
        <p className="flex-none px-3 pb-1 text-[11.5px] text-faint" data-testid="change-summary">
          {changes.length} file{changes.length === 1 ? '' : 's'} changed
          {(totals.added > 0 || totals.removed > 0) && (
            <>
              {' · '}
              <span className="text-added">+{totals.added}</span> <span className="text-removed">−{totals.removed}</span>
            </>
          )}
        </p>
      )}
      <div role="tree" aria-label={mode === 'changed' ? 'Changed files' : 'Files'} className="min-h-0 flex-1 overflow-y-auto px-1.5 pb-3">
        {reading ? (
          <p className="px-2 py-1 text-[12px] text-faint">Reading…</p>
        ) : rows.length ? (
          rows
        ) : (
          <p className="px-2 py-1 text-[12px] text-faint">{q ? 'No file matches.' : mode === 'changed' ? (commitId ? 'This commit changed no files.' : '') : 'No files.'}</p>
        )}
      </div>
    </div>
  );
}
